import clsx from 'clsx';
import React, { useEffect, useRef } from 'react';
import { Skeleton } from './Feedback';
import { UnreadMarker } from './Table';

/**
 * The compact list on the left of an inbox workspace (Texts, Voicemail).
 * Two lines in 64px: who and when, then a one-line preview with its chips.
 * Selected reads like the selected nav item (indigo tint, full-height bar);
 * unread keeps the table's short accent bar, a semibold name and an accent
 * time. Rows have no hover actions: click or Enter opens the record.
 */
export const InboxRow: React.FC<{
  selected: boolean;
  unread?: boolean;
  onOpen: () => void;
  avatar: React.ReactNode;
  title: React.ReactNode;
  /** A phone number standing in for a name: tabular, one step quieter. */
  anonymous?: boolean;
  /** Chips after the name (VIP). */
  badges?: React.ReactNode;
  /** ISO timestamp and its short label ("3h ago"). */
  at?: string | null;
  atLabel?: React.ReactNode;
  preview?: React.ReactNode;
  /** Chips at the end of the preview line (category, status, count). */
  trailing?: React.ReactNode;
}> = ({
  selected,
  unread,
  onOpen,
  avatar,
  title,
  anonymous,
  badges,
  at,
  atLabel,
  preview,
  trailing,
}) => {
  const ref = useRef<HTMLLIElement>(null);
  useEffect(() => {
    if (selected) ref.current?.scrollIntoView?.({ block: 'nearest' });
  }, [selected]);
  return (
    <li
      ref={ref}
      role="option"
      aria-selected={selected}
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => e.key === 'Enter' && onOpen()}
      className={clsx(
        'relative flex h-16 cursor-pointer items-center gap-3 border-b border-v2-line/60 px-4 outline-none transition-colors',
        selected
          ? 'bg-v2-accent/[0.08] before:absolute before:inset-y-0 before:left-0 before:w-[3px] before:bg-v2-accent'
          : 'hover:bg-v2-accent-soft focus-visible:bg-v2-sunken',
      )}
    >
      {unread && !selected && <UnreadMarker />}
      {avatar}
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="flex items-center gap-2">
          <span
            className={clsx(
              'min-w-0 truncate text-v2-14',
              anonymous && 'v2-num',
              unread
                ? 'font-semibold text-v2-fg'
                : anonymous
                  ? 'text-v2-fg-2'
                  : 'font-medium text-v2-fg',
            )}
          >
            {title}
          </span>
          {unread && <span className="sr-only">Unread</span>}
          {badges}
          {atLabel != null && (
            <time
              dateTime={at ?? undefined}
              className={clsx(
                'v2-num ml-auto shrink-0 text-v2-11',
                unread ? 'font-semibold text-v2-accent' : 'text-v2-fg-3',
              )}
            >
              {atLabel}
            </time>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span
            className={clsx(
              'min-w-0 flex-1 truncate text-v2-12',
              unread ? 'text-v2-fg-2' : 'text-v2-fg-3',
            )}
          >
            {preview}
          </span>
          {trailing}
        </div>
      </div>
    </li>
  );
};

/** A dated group of rows (Today, Yesterday) with a sticky micro-label. */
export const InboxGroup: React.FC<{
  label: string;
  count?: number;
  children: React.ReactNode;
}> = ({ label, count, children }) => (
  <section aria-label={label}>
    <h3 className="sticky top-0 z-[1] flex h-8 items-center gap-2 border-b border-v2-line/60 bg-v2-surface/90 px-4 text-v2-10 font-semibold uppercase tracking-[0.1em] text-v2-fg-3 backdrop-blur-sm">
      {label}
      {count != null && (
        <span className="v2-num rounded-full bg-v2-sunken px-1.5 text-v2-11 font-medium normal-case tracking-normal">
          {count}
        </span>
      )}
    </h3>
    <ul role="listbox" aria-label={label}>
      {children}
    </ul>
  </section>
);

export const InboxListSkeleton: React.FC<{ label: string; rows?: number }> = ({
  label,
  rows = 6,
}) => (
  <ul aria-label={label} className="flex flex-col">
    {Array.from({ length: rows }).map((_, i) => (
      <li
        key={i}
        className="flex h-16 items-center gap-3 border-b border-v2-line/60 px-4"
      >
        <Skeleton className="h-8 w-8 rounded-full" />
        <div className="flex-1">
          <Skeleton className="h-3" width={`${40 + (i % 3) * 12}%`} />
          <Skeleton className="mt-2 h-3" width={`${70 - (i % 4) * 10}%`} />
        </div>
      </li>
    ))}
  </ul>
);
